import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Share2,
  ShoppingBag,
  Briefcase,
  Users,
  CreditCard,
  Wallet,
  Bell,
  UserCircle,
  Building2,
  Settings as SettingsIcon,
  LogOut
} from 'lucide-react';
import { cn } from '../../utils/helpers';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from '../../hooks/useTranslation';

const navItems = [
  { icon: LayoutDashboard, labelKey: 'nav.dashboard', path: '/app' },
  { icon: MessageSquare, labelKey: 'nav.messaging', path: '/app/messaging' },
  { icon: Share2, labelKey: 'nav.social', path: '/app/social' },
  { icon: ShoppingBag, labelKey: 'nav.commerce', path: '/app/commerce' },
  { icon: Briefcase, labelKey: 'nav.career', path: '/app/career' },
  { icon: Building2, labelKey: 'nav.company', path: '/app/company' },
  { icon: Users, labelKey: 'nav.affiliate', path: '/app/affiliate' },
  { icon: CreditCard, labelKey: 'nav.payment', path: '/app/payment' },
  { icon: Wallet, labelKey: 'nav.wallet', path: '/app/wallet' },
  { icon: Bell, labelKey: 'nav.notifications', path: '/app/notifications' },
  { icon: UserCircle, labelKey: 'nav.profile', path: '/app/profile' },
  { icon: SettingsIcon, labelKey: 'nav.settings', path: '/app/settings' },
];

export default function TabsLayout() {
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const displayName = user?.displayName || user?.email?.split('@')[0] || 'Account';
  const email = user?.email ?? '';
  const avatar = user?.photoURL || `https://picsum.photos/seed/${encodeURIComponent(email || 'user')}/100/100`;

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-neutral-50 dark:bg-neutral-950">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 sticky top-0 h-screen">
        <div className="p-6 flex items-center gap-3">
          <div className="w-8 h-8 bg-neutral-900 dark:bg-neutral-100 rounded-lg flex items-center justify-center">
            <span className="text-white dark:text-neutral-900 font-bold text-sm">M</span>
          </div>
          <span className="font-bold text-lg tracking-tight dark:text-neutral-50">Messego</span>
        </div>

        <nav className="flex-1 px-4 py-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/app'}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-neutral-900 dark:bg-neutral-100 text-white dark:text-neutral-900 shadow-lg shadow-neutral-900/20 dark:shadow-neutral-100/10"
                  : "text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100"
              )}
            >
              <item.icon className="w-5 h-5" />
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-3 p-2">
            <NavLink to="/app/settings" className="flex items-center gap-3 flex-1 min-w-0 rounded-xl hover:bg-neutral-50 dark:hover:bg-neutral-800 p-1 -m-1 transition-colors">
              <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center overflow-hidden shrink-0">
                <img src={avatar} alt={displayName} referrerPolicy="no-referrer" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate dark:text-neutral-100">{displayName}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{email}</p>
              </div>
            </NavLink>
            <button
              onClick={handleSignOut}
              title={t('common.signOut')}
              aria-label={t('common.signOut')}
              className="p-2 rounded-lg text-neutral-400 dark:text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 pb-20 lg:pb-0">
        <header className="h-16 bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md border-bottom border-neutral-200 dark:border-neutral-800 sticky top-0 z-30 flex items-center justify-between px-6 lg:hidden">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-neutral-900 dark:bg-neutral-100 rounded-lg flex items-center justify-center">
              <span className="text-white dark:text-neutral-900 font-bold text-sm">M</span>
            </div>
            <span className="font-bold text-lg tracking-tight dark:text-neutral-50">Messego</span>
          </div>
          <button className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800">
            <Bell className="w-5 h-5 text-neutral-600 dark:text-neutral-300" />
          </button>
        </header>

        <div className="flex-1 p-4 lg:p-8 max-w-7xl mx-auto w-full">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800 px-2 py-1 z-40 flex justify-around items-center">
        {navItems.slice(0, 5).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/app'}
            className={({ isActive }) => cn(
              "flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-200",
              isActive ? "text-neutral-900 dark:text-neutral-50" : "text-neutral-400 dark:text-neutral-500"
            )}
          >
            {({ isActive }) => (
              <>
                <item.icon className={cn("w-6 h-6", isActive && "fill-neutral-900/10 dark:fill-neutral-50/10")} />
                <span className="text-[10px] font-medium">{t(item.labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
        <NavLink
          to="/app/profile"
          className={({ isActive }) => cn(
            "flex flex-col items-center gap-1 p-2 rounded-xl transition-all duration-200",
            isActive ? "text-neutral-900 dark:text-neutral-50" : "text-neutral-400 dark:text-neutral-500"
          )}
        >
          <UserCircle className="w-6 h-6" />
          <span className="text-[10px] font-medium">{t('nav.profile')}</span>
        </NavLink>
      </nav>
    </div>
  );
}
